import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import {
  materialRequestService,
  MaterialRequest,
  CreateMaterialRequestDTO,
  type MaterialRequestLineDimensions,
  InventoryAvailability,
  BoqEstimationPreviewLine,
} from '../services/materialRequest.service';
import workOrderService from '../services/workOrder.service';
import type { MaterialRequestBomOptionsPayload } from '../services/workOrder.service';
import type { RejectionPayload } from '../services/rejection.service';

export const useMaterialRequests = (filters?: {
  status?: string;
  priority?: string;
  startDate?: string;
  endDate?: string;
  project_id?: string;
  context_kind?: string;
  work_order_id?: string;
  fulfillmentQueue?: boolean;
}): UseQueryResult<MaterialRequest[], Error> => {
  return useQuery({
    queryKey: ['material-requests', filters],
    queryFn: () => materialRequestService.getAll(filters),
  });
};

export const useMaterialRequest = (id: string, options?: { enabled?: boolean }): UseQueryResult<MaterialRequest, Error> => {
  return useQuery({
    queryKey: ['material-requests', id],
    queryFn: () => materialRequestService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
};

export const useMaterialRequestsByWorkOrder = (workOrderId: string, options?: { enabled?: boolean }): UseQueryResult<MaterialRequest[], Error> => {
  return useQuery({
    queryKey: ['material-requests', 'work-order', workOrderId],
    queryFn: () => materialRequestService.getByWorkOrder(workOrderId),
    enabled: options?.enabled !== false && !!workOrderId,
  });
};

export const useBoqEstimationPreviewLines = (
  workOrderId: string,
  options?: { enabled?: boolean; production_unit_number?: number | null }
): UseQueryResult<BoqEstimationPreviewLine[], Error> => {
  return useQuery({
    queryKey: [
      'material-requests',
      'boq-estimation-preview',
      workOrderId,
      options?.production_unit_number ?? 'full-line',
    ],
    queryFn: () =>
      materialRequestService.previewBoqEstimation(workOrderId, {
        production_unit_number: options?.production_unit_number,
      }),
    enabled: options?.enabled !== false && !!workOrderId,
  });
};

export const useMaterialRequestBomOptions = (
  workOrderId: string,
  options?: { enabled?: boolean }
): UseQueryResult<MaterialRequestBomOptionsPayload, Error> => {
  return useQuery({
    queryKey: ['material-request-bom-options', workOrderId],
    queryFn: () => workOrderService.getMaterialRequestBomOptions(workOrderId),
    enabled: options?.enabled !== false && !!workOrderId,
  });
};

export const useCreateMaterialRequest = (): UseMutationResult<MaterialRequest, Error, CreateMaterialRequestDTO> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => materialRequestService.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', 'work-order', variables.work_order_id] });
      message.success('Material request created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create material request');
    },
  });
};

export const useCreateMaterialRequestFromBOM = (): UseMutationResult<
  MaterialRequest,
  Error,
  {
    workOrderId: string;
    warehouseId?: string;
    bomId?: string;
    bomLineIds?: string[];
    requiredDate?: string;
    priority?: 'Low' | 'Normal' | 'High' | 'Urgent';
    notes?: string;
    departmentId?: string;
    bomLineTraceability?: Record<string, { heat_number?: string; dimension?: string }>;
    bomLineAreaDimension?: Record<string, number>;
    bomLineDimensions?: Record<string, MaterialRequestLineDimensions>;
    workOrderLineByBomLineId?: Record<string, string>;
  }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({
      workOrderId,
      warehouseId,
      bomId,
      bomLineIds,
      requiredDate,
      priority,
      notes,
      departmentId,
      bomLineTraceability,
      bomLineAreaDimension,
      bomLineDimensions,
      workOrderLineByBomLineId,
    }) =>
      materialRequestService.createFromBOM(workOrderId, {
        warehouse_id: warehouseId,
        bom_id: bomId,
        bom_line_ids: bomLineIds,
        required_date: requiredDate,
        priority,
        notes,
        department_id: departmentId,
        bom_line_traceability: bomLineTraceability,
        bom_line_area_dimension: bomLineAreaDimension,
        bom_line_dimensions: bomLineDimensions,
        work_order_line_by_bom_line_id: workOrderLineByBomLineId,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', 'work-order', variables.workOrderId] });
      message.success('Material request created from BOM successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create material request from BOM');
    },
  });
};

export const useCreateMaterialRequestFromBoqEstimation = (): UseMutationResult<
  MaterialRequest,
  Error,
  {
    workOrderId: string;
    warehouseId?: string;
    requiredDate?: string;
    priority?: 'Low' | 'Normal' | 'High' | 'Urgent';
    notes?: string;
    departmentId?: string;
    lineKeys?: string[];
    itemIdByLineKey?: Record<string, string>;
    boqLineTraceability?: Record<string, { heat_number?: string; dimension?: string }>;
    boqLineAreaDimension?: Record<string, number>;
    boqLineDimensions?: Record<string, MaterialRequestLineDimensions>;
    production_unit_number?: number | null;
    workOrderLineByLineKey?: Record<string, string>;
  }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({
      workOrderId,
      warehouseId,
      requiredDate,
      priority,
      notes,
      departmentId,
      lineKeys,
      itemIdByLineKey,
      boqLineTraceability,
      boqLineAreaDimension,
      boqLineDimensions,
      production_unit_number,
      workOrderLineByLineKey,
    }) =>
      materialRequestService.createFromBoqEstimation(workOrderId, {
        warehouse_id: warehouseId,
        required_date: requiredDate,
        priority,
        notes,
        department_id: departmentId,
        line_keys: lineKeys,
        item_id_by_line_key: itemIdByLineKey,
        boq_line_traceability: boqLineTraceability,
        boq_line_area_dimension: boqLineAreaDimension,
        boq_line_dimensions: boqLineDimensions,
        production_unit_number,
        work_order_line_by_line_key: workOrderLineByLineKey,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', 'work-order', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', 'boq-estimation-preview', variables.workOrderId] });
      message.success('Material request created from BOQ estimation');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create material request from BOQ estimation');
    },
  });
};

export const useUpdateMaterialRequest = (): UseMutationResult<MaterialRequest, Error, { id: string; data: Partial<CreateMaterialRequestDTO> }> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }) => materialRequestService.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', data.id] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', 'work-order', data.work_order_id] });
      message.success('Material request updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update material request');
    },
  });
};

export const useSubmitMaterialRequest = (): UseMutationResult<MaterialRequest, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => materialRequestService.submitForApproval(id),
    onSuccess: (data) => {
      if (data?.id) {
        queryClient.setQueryData(['material-requests', data.id], data);
      }
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      const autoApproved =
        data?.request_status === 'Approved' &&
        (data?.approval_status === 'Not Required' || data?.approval_status === 'Approved');
      message.success(
        autoApproved
          ? 'Material request submitted and approved (below approval threshold)'
          : 'Material request submitted for approval'
      );
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to submit material request');
    },
  });
};

export const useApproveMaterialRequest = (): UseMutationResult<MaterialRequest, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => materialRequestService.approve(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', data.id] });
      message.success('Material request approved');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to approve material request');
    },
  });
};

export const useRejectMaterialRequest = (): UseMutationResult<
  MaterialRequest,
  Error,
  { id: string; payload: RejectionPayload }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }) => materialRequestService.reject(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', data.id] });
      message.success('Material request rejected');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject material request');
    },
  });
};

export const useCancelMaterialRequest = (): UseMutationResult<MaterialRequest, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => materialRequestService.cancel(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', data.id] });
      message.success('Material request cancelled');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to cancel material request');
    },
  });
};

export const useCheckInventoryAvailability = (): UseMutationResult<
  InventoryAvailability[],
  Error,
  Array<{
    item_id: string;
    requested_quantity: number;
    warehouse_id?: string;
    heat_number?: string;
    dimension?: string;
  }>
> => {
  return useMutation({
    mutationFn: (items) => materialRequestService.checkInventoryAvailability(items),
  });
};
